import { useMemo } from 'react';
import { EChart as ReactECharts } from '../../charts/EChart';
import { useAppContext } from '../../../providers/AppProvider';
import { formatLength, toKm } from '../../../helpers/formatLength';

/**
 * The order book month by month, stacked by what happened to it.
 *
 * A bar's height is what was sold that month; its colour tells you how much of
 * it the factory actually turned into cable. A run of tall bars going yellow at
 * the top is the thing worth catching early — demand arriving faster than it is
 * being converted — and no single-series volume chart shows it.
 *
 * The order count rides on a second axis, because ten small orders and one big
 * one are different problems for the people scheduling them.
 */

const SERIES = [
  { key: 'produced', name: 'Made', color: 'success' },
  { key: 'stock', name: 'From stock', color: 'primary' },
  { key: 'promised', name: 'Promised', color: 'info' },
  { key: 'open', name: 'Nothing yet', color: 'warning' }
];

const OrderVolumeChart = ({ months = [], style }) => {
  const { getThemeColor } = useAppContext();

  const option = useMemo(() => {
    const axis = { color: getThemeColor('gray-800'), fontFamily: 'Nunito Sans' };

    return {
      grid: { left: 6, right: 6, top: 32, bottom: 4, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        padding: [8, 12],
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-300'),
        borderWidth: 1,
        textStyle: { color: getThemeColor('dark'), fontFamily: 'Nunito Sans' },
        formatter: params => {
          const bucket = months[params?.[0]?.dataIndex];
          if (!bucket) return '';
          const rows = params
            .filter(p => p.seriesType === 'bar' && p.value > 0)
            .map(
              p =>
                `${p.marker} ${p.seriesName}: <strong>${formatLength(
                  bucket[SERIES.find(s => s.name === p.seriesName)?.key]
                )}</strong>`
            )
            .join('<br/>');
          return `<strong>${bucket.label}</strong> — ${
            bucket.orders
          } order${bucket.orders === 1 ? '' : 's'}<br/>${rows}<br/>
            <span class="text-body-tertiary">total ${formatLength(
              bucket.ordered
            )}</span>`;
        }
      },
      legend: {
        top: 0,
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { ...axis, fontWeight: 600 }
      },
      xAxis: {
        type: 'category',
        data: months.map(m => m.label),
        axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
        axisTick: { show: false },
        axisLabel: { ...axis, fontWeight: 600 }
      },
      yAxis: [
        {
          type: 'value',
          name: 'km',
          nameTextStyle: { ...axis, fontSize: 11 },
          splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
          axisLabel: axis
        },
        {
          type: 'value',
          name: 'orders',
          nameTextStyle: { ...axis, fontSize: 11 },
          minInterval: 1,
          splitLine: { show: false },
          axisLabel: axis
        }
      ],
      series: [
        ...SERIES.map((s, i) => ({
          name: s.name,
          type: 'bar',
          stack: 'book',
          barMaxWidth: 34,
          itemStyle: {
            color: getThemeColor(s.color),
            // Only the top of the stack is rounded, and which series that is
            // depends on the month — so round the last one and let a zero-height
            // segment below it be invisible either way.
            borderRadius: i === SERIES.length - 1 ? [4, 4, 0, 0] : 0
          },
          data: months.map(m => toKm(m[s.key]))
        })),
        {
          name: 'Orders',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          symbolSize: 7,
          lineStyle: { width: 2, type: 'dashed' },
          itemStyle: { color: getThemeColor('gray-700') },
          data: months.map(m => m.orders)
        }
      ]
    };
  }, [months, getThemeColor]);

  if (!months.length) {
    return (
      <div className="text-center text-body-tertiary py-6 fs-9">
        Nothing dated in this period.
      </div>
    );
  }

  return <ReactECharts option={option} style={style} />;
};

export default OrderVolumeChart;
